import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowUpRight } from "lucide-react";

export default function SendMoneyModal({ open, onClose, onSuccess, presetRecipient }) {
  const { t } = useApp();
  const [recipient, setRecipient] = useState(presetRecipient || "");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (open) setRecipient(presetRecipient || "");
  }, [open, presetRecipient]);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!recipient.trim() || !amt || amt <= 0) return;
    setLoading(true);
    try {
      const res = await api.post("/wallet/send", { recipient: recipient.trim(), amount: amt, note });
      toast.success(`${t.toastSent} · ${res.data.recipient}`);
      setRecipient(""); setAmount(""); setNote("");
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="send-money-modal" className="max-w-sm rounded-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowUpRight className="w-5 h-5 text-primary" /> {t.sendMoney}
          </DialogTitle>
          <DialogDescription>{t.sendMoneyDesc}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3.5 pt-1">
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t.recipient}</label>
            <Input
              data-testid="send-recipient-input"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder={t.recipientPlaceholder}
              className="h-12 rounded-2xl mt-1.5"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t.amount}</label>
            <div className="relative mt-1.5">
              <Input
                data-testid="send-amount-input"
                type="number"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={t.enterAmount}
                className="h-12 rounded-2xl text-lg font-bold tabular-nums"
              />
              <span className="absolute top-1/2 -translate-y-1/2 end-4 text-sm font-bold text-muted-foreground">{t.currency}</span>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t.note}</label>
            <Input
              data-testid="send-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t.notePlaceholder}
              className="h-12 rounded-2xl mt-1.5"
            />
          </div>
          <Button
            data-testid="send-money-form-submit"
            onClick={submit}
            disabled={loading || !recipient || !amount}
            className="w-full h-[52px] rounded-2xl text-base font-bold"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t.send}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
